import { IsActiveMatchOptions } from '@angular/router';
import { assign, cloneDeep, isObject, keys, merge, reduce } from 'lodash-es';
import { isMoment } from 'moment';
import mime from 'mime';
import { FormGroup } from '@angular/forms';
import { validateNRIC, validateUEN } from '@core/util';

export const SCREENWIDTH = {
  MOBILE: 768,
  TABLET: 992,
};

export class Helper {
  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = true".
   */
  static get exactMatchOptions(): IsActiveMatchOptions {
    return {
      paths: 'exact',
      fragment: 'ignored',
      matrixParams: 'ignored',
      queryParams: 'exact',
    };
  }

  /**
   * Get the equivalent "IsActiveMatchOptions" options for "exact = false".
   */
  static get subsetMatchOptions(): IsActiveMatchOptions {
    return {
      paths: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
      queryParams: 'subset',
    };
  }

  static createFileFromBase64(base64: string, fileName: string): File {
    const byte = atob(base64.split(',')[1]);
    const mimetype = base64.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byte.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byte.length; i++) {
      ia[i] = byte.charCodeAt(i);
    }

    const extension = mime.getExtension(mimetype);
    const hasCorrectExtension = fileName.endsWith(`.${extension}`);
    const finalFileName = hasCorrectExtension ? fileName : `${fileName}.${extension}`;

    return new File([ab], finalFileName, { type: mimetype });
  }

  static createFileFromBlob(blob: Blob, fileName: string): File {
    return new File([blob], [fileName, mime.getExtension(blob.type)].filter(Boolean).join('.'), {
      lastModified: new Date().getTime(),
      type: blob.type,
    });
  }

  static openInNewTab(blob: Blob | MediaSource, target: '_self' | '_blank' | '_parent' | '_top' | '_unfencedTop') {
    if (!(blob instanceof Blob) && !(blob instanceof MediaSource)) {
      throw new Error('Expected Blob or MediaSource');
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.target = target;
    link.click();

    URL.revokeObjectURL(url);
  }

  static getScreenWidth() {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  }

  static isMobileScreen = () => this.getScreenWidth() < SCREENWIDTH.MOBILE;
  static isTabletScreen = () => this.getScreenWidth() < SCREENWIDTH.TABLET && this.getScreenWidth() >= SCREENWIDTH.MOBILE;
  static isDesktopScreen = () => this.getScreenWidth() > SCREENWIDTH.TABLET;

  static removeCommaFromText(txt: string) {
    let finalString = txt;
    if (txt && Object.prototype.toString.call(txt) === '[object String]') {
      finalString = finalString.replace(/,/g, '');
    }
    return +finalString;
  }

  static removeEmpty(data: any, exclude: string[] = []): any {
    if (!data) {
      return data;
    }
    switch (typeof data) {
      case 'object': {
        if (Array.isArray(data)) {
          return data.filter(item => !(item === null || item === undefined || item === '' || (Array.isArray(item) && !item.length)));
        }

        Object.keys(data).forEach(key => {
          if (!exclude.includes(key)) {
            const val = Helper.removeEmpty(data[key], exclude);
            if (val === null || val === undefined || val === '' || (Array.isArray(val) && !val.length)) {
              delete data[key];
            } else {
              data[key] = val;
            }
          }
        });
        return data;
      }
      case 'string': {
        return data.trim();
      }
      default:
        return data;
    }
  }

  static flatParams(params: Record<string, any>): object {
    if (!params) {
      return params;
    }
    Object.keys(params).forEach(key => {
      if (Array.isArray(params[key])) {
        params[key] = params[key].join(',');
      }
    });
    return params;
  }

  static slugify(input: string): string {
    return (
      input
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        // eslint-disable-next-line no-useless-escape
        .replace(/\-\-+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, '')
        .replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
        .replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
        .replace(/ì|í|ị|ỉ|ĩ/g, 'i')
        .replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o')
        .replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u')
        .replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
        .replace(/đ/g, 'd')
        // eslint-disable-next-line no-useless-escape
        .replace(/[^\w\-]+/g, '')
    ); // Remove all non-word chars;
  }

  static camelize(input: string): string {
    return (
      Helper.slugify(input)
        // eslint-disable-next-line no-useless-escape
        .replace(/\-(.)/g, char => char.toUpperCase()) // UpperCase first character
        .replace(/-/g, '')
    ); // Remove -
  }

  static groupBy(list: any[], keyGetter: (data: any) => string, converter?: (data: any) => any) {
    const map = new Map();
    list.forEach(item => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [converter ? converter(item) : item]);
      } else {
        collection.push(converter ? converter(item) : item);
      }
    });
    return map;
  }

  static isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  static mergeDeep(target: any, source: any): any {
    const output = Object.assign({}, target);
    if (Helper.isObject(target) && Helper.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (Helper.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = Helper.mergeDeep(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }

  static leftJoin(target: any, source: any): any {
    const output = Object.assign({}, target);
    if (Helper.isObject(target) && Helper.isObject(source)) {
      Object.keys(target).forEach(key => {
        if (Helper.isObject(target[key])) {
          if (!(key in source)) {
            Object.assign(output, { [key]: target[key] });
          } else {
            output[key] = Helper.mergeDeep(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] || target[key] });
        }
      });
    }
    return output;
  }

  static isEqual(target: any, source: any): boolean {
    const targetType = typeof target;
    const sourceType = typeof source;
    if (targetType !== sourceType) {
      console.log('khac type', target, source);
      return false;
    }
    if (Array.isArray(target) && Array.isArray(source)) {
      const length = target.length;
      if (length !== source.length) {
        console.log('khac len');
        return false;
      }
      let check = true;
      for (let i = 0; i < length; i++) {
        check = Helper.isEqual(target[i], source[i]);
        if (!check) {
          break;
        }
      }
      return check;
    }

    if (Helper.isObject(target) && Helper.isObject(source)) {
      const targetValue = Object.values(target);
      const sourceValue = Object.values(source);
      return Helper.isEqual(targetValue, sourceValue);
    }

    return target === source;
  }

  static isEmpty(input: any): boolean {
    if (!input) {
      return true;
    }
    if (Array.isArray(input)) {
      return input.length === 0;
    }
    if (typeof input === 'object') {
      return Object.keys(input).length === 0;
    }
    return false;
  }

  static transformAddressObjectToString(objectAddress: {
    postalCode?: string;
    unitNumber?: string;
    blockNumber?: string;
    streetName?: string;
    buildingName?: string;
  }): string {
    const str =
      (objectAddress?.blockNumber ? objectAddress?.blockNumber : '') +
      ' ' +
      (objectAddress?.streetName ? objectAddress?.streetName : '') +
      ' ' +
      (objectAddress?.unitNumber ? objectAddress?.unitNumber : '') +
      ' ' +
      (objectAddress?.buildingName ? objectAddress?.buildingName : '') +
      ' SINGAPORE ' +
      (objectAddress?.postalCode ? objectAddress?.postalCode : '');
    return str.replace(/[^\S\r\n]{2,}/, ' ').trim();
  }

  static decodeJWT(token: string) {
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      return false;
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    try {
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return false;
    }
  }

  // Decode a URL-safe base64 string
  static decodeBase64Url(payload: string): string {
    // Replace URL-safe characters
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Pad with '=' to make length a multiple of 4
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    // atob works in browser environments (Angular). Decode percent-encoded UTF-8 bytes safely.
    try {
      const binary = atob(base64);
      // Convert binary string to utf-8
      const bytes = Array.prototype.map.call(binary, (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('');
      return decodeURIComponent(bytes);
    } catch (e) {
      // Fallback: return raw atob result or throw
      try {
        return atob(base64);
      } catch {
        throw new Error('Invalid base64 string');
      }
    }
  }

  // Validate JWT: ensure it has three parts and the `exp` claim (expiry) is in the future
  static validateJwt(tokenLike: string): boolean {
    try {
      // Accept tokens with optional 'Bearer ' prefix
      const token = tokenLike.startsWith('Bearer ') ? tokenLike.slice(7) : tokenLike;
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      const payload = parts[1];
      const decoded = this.decodeBase64Url(payload);
      const obj = JSON.parse(decoded);

      if (!obj || typeof obj !== 'object') return false;

      // exp is expected to be seconds since epoch
      if (typeof obj.exp !== 'number') return false;

      const now = Math.floor(Date.now() / 1000);
      return obj.exp > now;
    } catch (e) {
      return false;
    }
  }

  /**
   * Generates a random id
   *
   * @param length
   */
  static randomId(length: number = 10): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let name = '';

    for (let i = 0; i < length; i++) {
      name += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return name;
  }

  static clearMoment(data: any, config?: { milliseconds: boolean }): any {
    if (isMoment(data)) {
      if (config?.milliseconds) {
        return data.valueOf();
      }
      return data.toDate().toISOString();
    }
    if (Array.isArray(data)) {
      return data.map(item => Helper.clearMoment(item, config));
    }
    if (data instanceof Date) {
      return data.toISOString();
    }
    if (isObject(data)) {
      const res = reduce(
        keys(cloneDeep(data)).map(key => {
          return { [key]: Helper.clearMoment((data as Record<string, unknown>)[key], config) };
        }),
        (acc, item) => {
          return assign(acc, item);
        },
        {},
      );
      return res;
    }

    return data;
  }

  static momentToDate(data: any): any {
    if (isMoment(data)) {
      return data.toDate();
    }
    if (Array.isArray(data)) {
      return data.map(item => Helper.momentToDate(item));
    }
    if (data instanceof Date) {
      return data;
    }
    if (isObject(data)) {
      const res = reduce(
        keys(cloneDeep(data)).map(key => {
          return { [key]: Helper.momentToDate((data as Record<string, unknown>)[key]) };
        }),
        (acc, item) => {
          return assign(acc, item);
        },
        {},
      );
      return res;
    }

    return data;
  }

  static round(num: number, precision: number) {
    return precision > -1 ? Math.round((num + Number.EPSILON) * Math.pow(10, precision)) / Math.pow(10, precision) : num;
  }

  static floor(num: number, precision: number) {
    return precision > -1 ? Math.floor((num + Number.EPSILON) * Math.pow(10, precision)) / Math.pow(10, precision) : num;
  }

  static isNricNumber(nric: string): boolean {
    return validateNRIC(nric);
  }

  static isUENNumber(uen: string): boolean {
    return validateUEN(uen);
  }

  static toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      value = value.toString();
    }

    if (typeof value === 'string') {
      switch (value?.toLowerCase()?.trim()) {
        case 'true':
        case 'yes':
        case 'y':
        case '1':
          return true;
        case 'false':
        case 'no':
        case 'n':
        case '0':
        case 'null':
        case 'undefined':
          return false;
        default:
          return false;
      }
    }

    // TODO: handle value is function or object

    return false;
  }

  static logFormError(name: string, form: FormGroup): void {
    console.group(name, 'Form Error');
    for (const key in form.controls) {
      if (Object.prototype.hasOwnProperty.call(form.controls, key)) {
        const element = form.controls[key];
        if (element.invalid) console.log(key, element.value, element.errors);
      }
    }
    console.groupEnd();
  }

  static getFileNameWithoutExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // If there is no dot in the filename, return the filename itself
      return filename;
    } else {
      // Otherwise, return the filename up to the last dot
      return filename.substring(0, lastDotIndex);
    }
  }

  static getFileExtension(filename: string): string | null {
    return filename.split('.').pop() || null;
  }

  // Add a plus sign at the beginning
  static addPlusSignAtTheBeginning(areaCode: string): string {
    return `+${areaCode.replace(/\+/g, '')}`;
  }

  static addComma(str: number): string {
    const rgx = /(\d+)(\d{3})/;
    const x: string[] = str.toFixed(2).split('.');

    let x1: string = x[0];
    const x2: string = x.length > 1 ? '.' + x[1] : '';

    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1' + ',' + '$2');
    }

    return x1 + x2;
  }

  static addCurrency(nbr: number): string {
    return nbr > 0 ? '$' : '';
  }

  static mergeDeepWithoutMergeArray(objValue: any, srcValue: any) {
    // Ignore falsy srcValue
    if (!srcValue) {
      return objValue;
    }

    if (Array.isArray(objValue) && Array.isArray(srcValue)) {
      return srcValue;
    }

    // If both values are objects, merge them
    if (isObject(objValue) && isObject(srcValue)) {
      return merge({}, objValue, srcValue);
    }

    // Otherwise, return srcValue (overwriting the existing value)
    return srcValue;
  }
}
